import { serviceUrls } from "./constant";
import {
  get,
  put,
  post,
  getLocalStorageData,
} from "../services/axiosComponent";
import { prepareRegionList } from "./utilityFunctions";
import {createQueryParams} from "../services/axiosComponent";
// Student-specific localStorage keys to avoid interference with admin flow
 const STUDENT_STORAGE_KEYS = {
  isStudent: "isStudent",
  ACCESS_TOKEN: "studentAccessToken",
  REFRESH_TOKEN: "studentRefreshToken",
  PROFILE_DETAILS: "studentProfileDetails",
  EXAM_SESSION: "studentExamSession",
  PREFERENCES: "studentPreferences",
};



export const studentLogin = async (data) => {
  try {
    // Use different endpoint for student login or add student flag
    var res = await post({
      url: serviceUrls.ADMIN_SERVICE + "/open/token",
      data: {
        ...data,
        userType: "STUDENT", // Add student identifier
      },
    });

    if (res.status === "success") {
      console.log("Student logged in successfully:", res);

      // Store student tokens with different keys
   await localStorage.setItem(
     STUDENT_STORAGE_KEYS.ACCESS_TOKEN,
     res.data.accessToken
   );
   await localStorage.setItem(
     STUDENT_STORAGE_KEYS.REFRESH_TOKEN,
     res.data.refreshToken
   );
   await localStorage.setItem(STUDENT_STORAGE_KEYS.isStudent, "true");

      console.log("Student tokens saved");

      // Get student profile details
      return await getStudentProfileDetails();
    }
    return res;
  } catch (error) {
    console.error("Student login error:", error);
    return {
      status: "error",
      message: "Login failed. Please try again.",
    };
  }
};

export const getStudentProfileDetails = async () => {
  try {
    // Use student-specific profile endpoint
    console.log("Fetching student profile details...");

    var res = await get({
      url: serviceUrls.ADMIN_SERVICE + "/default/user/profile",
      headers: {
        // Authorization: `Bearer ${localStorage.getItem(
        //   STUDENT_STORAGE_KEYS.ACCESS_TOKEN
        // )}`,
      },
    });

    if (res.status === "success") {
      console.log("Student profile details:", res);

      // Store student profile with different key
      localStorage.setItem(
        STUDENT_STORAGE_KEYS.PROFILE_DETAILS,
        JSON.stringify(res.data)
      );

      // Store additional student-specific data
      const studentData = {
        ...res.data,
        loginTime: new Date().toISOString(),
        userType: "STUDENT",
      };

      localStorage.setItem(
        STUDENT_STORAGE_KEYS.PROFILE_DETAILS,
        JSON.stringify(studentData)
      );

      console.log("Student profile saved:", studentData);
    }
    return res;
  } catch (error) {
    console.error("Error fetching student profile:", error);
    return {
      status: "error",
      message: "Failed to fetch profile details",
    };
  }
};

export const getStudentLocalStorageData = (key) => {
  try {
    const data = localStorage.getItem(STUDENT_STORAGE_KEYS[key.toUpperCase()]);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error getting student localStorage data:", error);
    return null;
  }
};

export const setStudentLocalStorageData = (key, data) => {
  try {
    localStorage.setItem(
      STUDENT_STORAGE_KEYS[key.toUpperCase()],
      JSON.stringify(data)
    );
    return true;
  } catch (error) {
    console.error("Error setting student localStorage data:", error);
    return false;
  }
};

export const studentLogout = () => {
  try {
    // Clear all student-related localStorage items
    Object.values(STUDENT_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    // Clear any additional student session data
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("student")) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));

    console.log("Student logout successful");
    return true;
  } catch (error) {
    console.error("Error during student logout:", error);
    return false;
  }
};

export const isStudentAuthenticated = () => {
  const token = localStorage.getItem(STUDENT_STORAGE_KEYS.ACCESS_TOKEN);
  const profile = localStorage.getItem(STUDENT_STORAGE_KEYS.PROFILE_DETAILS);
  return !!(token && profile);
};

export const getStudentAuthToken = () => {
  return localStorage.getItem(STUDENT_STORAGE_KEYS.ACCESS_TOKEN);
};

export const refreshStudentToken = async () => {
  try {
    const refreshToken = localStorage.getItem(
      STUDENT_STORAGE_KEYS.REFRESH_TOKEN
    );
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const res = await post({
      url: serviceUrls.ADMIN_SERVICE + "/open/student/refresh",
      data: { refreshToken },
    });

    if (res.status === "success") {
      localStorage.setItem(
        STUDENT_STORAGE_KEYS.ACCESS_TOKEN,
        res.data.accessToken
      );
      if (res.data.refreshToken) {
        localStorage.setItem(
          STUDENT_STORAGE_KEYS.REFRESH_TOKEN,
          res.data.refreshToken
        );
      }
      return res;
    }

    throw new Error(res.message || "Token refresh failed");
  } catch (error) {
    console.error("Student token refresh error:", error);
    studentLogout(); // Clear invalid tokens
    return {
      status: "error",
      message: "Session expired. Please login again.",
    };
  }
};

// Student-specific exam session management
export const startExamSession = (examData) => {
  try {
    const sessionData = {
      examId: examData.examId,
      examName: examData.examName,
      startTime: new Date().toISOString(),
      duration: examData.duration,
      studentId: getStudentLocalStorageData("PROFILE_DETAILS")?._ID,
      status: "IN_PROGRESS",
    };

    localStorage.setItem(
      STUDENT_STORAGE_KEYS.EXAM_SESSION,
      JSON.stringify(sessionData)
    );
    return sessionData;
  } catch (error) {
    console.error("Error starting exam session:", error);
    return null;
  }
};

export const getActiveExamSession = () => {
  try {
    const sessionData = localStorage.getItem(STUDENT_STORAGE_KEYS.EXAM_SESSION);
    return sessionData ? JSON.parse(sessionData) : null;
  } catch (error) {
    console.error("Error getting active exam session:", error);
    return null;
  }
};

export const endExamSession = () => {
  try {
    const sessionData = getActiveExamSession();
    if (sessionData) {
      sessionData.endTime = new Date().toISOString();
      sessionData.status = "COMPLETED";

      // Store completed session for reference
      const completedSessions = JSON.parse(
        localStorage.getItem("studentCompletedSessions") || "[]"
      );
      completedSessions.push(sessionData);
      localStorage.setItem(
        "studentCompletedSessions",
        JSON.stringify(completedSessions)
      );
    }

    // Clear active session
    localStorage.removeItem(STUDENT_STORAGE_KEYS.EXAM_SESSION);
    return true;
  } catch (error) {
    console.error("Error ending exam session:", error);
    return false;
  }
};

// Student preferences management
export const setStudentPreferences = (preferences) => {
  try {
    const currentPrefs = getStudentPreferences();
    const updatedPrefs = { ...currentPrefs, ...preferences };
    localStorage.setItem(
      STUDENT_STORAGE_KEYS.PREFERENCES,
      JSON.stringify(updatedPrefs)
    );
    return true;
  } catch (error) {
    console.error("Error setting student preferences:", error);
    return false;
  }
};

export const getStudentPreferences = () => {
  try {
    const prefs = localStorage.getItem(STUDENT_STORAGE_KEYS.PREFERENCES);
    return prefs
      ? JSON.parse(prefs)
      : {
          theme: "light",
          fontSize: "medium",
          notifications: true,
          autoSave: true,
        };
  } catch (error) {
    console.error("Error getting student preferences:", error);
    return {
      theme: "light",
      fontSize: "medium",
      notifications: true,
      autoSave: true,
    };
  }
};

// Exam-specific API calls (if needed)
export const getStudentExams = async () => {
  try {
    const res = await get({
      url: serviceUrls.ADMIN_SERVICE + "/default/student/exams",
   
    });
    return res;
  } catch (error) {
    console.error("Error fetching student exams:", error);
    return {
      status: "error",
      message: "Failed to fetch exams",
    };
  }
};

export const submitExamAnswers = async (examId, answers) => {
  try {
    const res = await post({
      url:
        serviceUrls.ADMIN_SERVICE + `/default/student/exams/${examId}/submit`,
      data: { answers },
     
    });
    return res;
  } catch (error) {
    console.error("Error submitting exam answers:", error);
    return {
      status: "error",
      message: "Failed to submit exam",
    };
  }
};

export const getExamResults = async (examId) => {
  try {
    const res = await get({
      url:
        serviceUrls.ADMIN_SERVICE + `/default/student/exams/${examId}/results`,
     
    });
    return res;
  } catch (error) {
    console.error("Error fetching exam results:", error);
    return {
      status: "error",
      message: "Failed to fetch results",
    };
  }
};

export const getStudentDetails = async (params) => {
  return await get({ url: serviceUrls.ACADEMIC_SERVICE + "/default/student", params });
};


//Examination LIST
export const getExaminationList = async (data) => {
  data.regionId = getLocalStorageData(STUDENT_STORAGE_KEYS.PROFILE_DETAILS)[
    "_REGION"
  ];
  data.siteId = getLocalStorageData(STUDENT_STORAGE_KEYS.PROFILE_DETAILS)[
    "_SITE"
  ];
  return await get({
    url: serviceUrls.EXAMINATION_SERVICE + "/student/examination/list",
    params: createQueryParams(data),
  });
};


// Export storage keys for external use if needed
export { STUDENT_STORAGE_KEYS };
